import React, { useState } from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import PersonalDetails from "./PersonalDetails/PersonalDetails.component";
import EducationDetails from "./EducationDetails/EducationDetails.component";
import ContactDetails from "./ContactDetails/ContactDetails.component";
import IdentityDetails from "./IdentityDetails/IdentityDetails.component";
import useTranslation from '../../../translations/use-translation';
import { getLoggedInUserInfo } from '../../../utils/authUtil';
import "./UserBasicProfile.styles.scss";

const loadExtendedPersonal = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('userExtendedProfile') || '{}');
    return saved.personal || null;
  } catch { return null; }
};

const UserBasicProfile = () => {
  const translate  = useTranslation();
  const steps = [
    translate('PERSONAL'),
    translate('CONTACT'),
    translate('EDUCATION'),
    translate('IDENTITY')
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const savedPersonal = loadExtendedPersonal();
  const authUser = getLoggedInUserInfo();
    const [userProfile, setUserProfile] = useState([
      {
        personal: {
          fullName: savedPersonal?.fullName  || authUser?.displayName || '',
          email:    savedPersonal?.email     || authUser?.email       || '',
          gender:   savedPersonal?.gender    || '',
          dob:      savedPersonal?.dob       || '',
          mobile:   savedPersonal?.mobile    || '',
          category: savedPersonal?.category  || 'general',
        },
        contacts: {
          alternateEmail: "",
          alternateMobile: "",
          currentAddress: "",
          cityDistrict: "",
          pinCode: "",
          state: "",
          country: ""
        },
        education: {
          secondaryPercentage: "",
          secondaryCgpa: "",
          secondaryYop: "", 

          seniorSecondaryCourseName: "",
          seniorSecondaryPercentage: "",
          seniorSecondaryCgpa: "", 
          seniorSecondaryYop: "", 

          diplomaCourseName: "",
          diplomaBranch: "",
          diplomaPercentage: "",
          diplomaCgpa: "", 
          diplomaYop: "", 

          graduationCourseName: "",
          graduationPercentage: "",
          graduationCgpa: "", 
          graduationYop: "", 

          postGraduationCourseName: "",
          postGraduationPercentage: "",
          postGraduationCgpa: "", 
          postGraduationYop: "", 
          
          phdCourseName: "",
          phdPercentage: "",
          phdCgpa: "",
          phdYop: "",

        },
        identity: {
          aadharNumber: "",
          panNumber: ""
        }
      },
    ]);

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };
  const getProfilePage = (step) => {
    switch (step) {
      case 0:
        return <PersonalDetails userProfile={userProfile} setUserProfile={setUserProfile} />;
      case 1:
        return <ContactDetails userProfile={userProfile} setUserProfile={setUserProfile} />;
      case 2:
        return <EducationDetails userProfile={userProfile} setUserProfile={setUserProfile}/>;
      default:
        return <IdentityDetails userProfile={userProfile} setUserProfile={setUserProfile}/>;
    }
  };
  return (
    <div className="user-basic-profile-container">
      <div className="profile-stepper-wrapper">
        <div className="stepper-header">
          <h2 className="stepper-title">Complete Your Profile</h2>
        </div>
        
        <div className="profile-progress">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <span className="profile-progress__label">
              Profile completeness
            </span>
            <span className="profile-progress__frac">
              {completedSteps()} / {totalSteps()} sections complete
            </span>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(completedSteps() / totalSteps()) * 100}
            sx={{
              height: 8,
              borderRadius: 999,
              backgroundColor: 'rgba(12,74,80,0.10)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                background: 'linear-gradient(90deg, #0B4A50, #1a8f9e)',
              },
            }}
          />
        </div>

        <div className="stepper-content">
          <Stepper nonLinear activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={label} completed={completed[index]}>
                <StepButton onClick={handleStep(index)}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>

          <div className="step-content">
            {allStepsCompleted() ? (
              <div className="completion-message">
                <h3 className="completion-title">Profile Complete!</h3>
                <p className="completion-text">
                  You have successfully completed all profile sections. 
                  You can review your information or reset to make changes.
                </p>
                <div className="navigation-controls">
                  <div className="nav-spacer"></div>
                  <Button 
                    onClick={handleReset}
                    className="nav-button btn-reset"
                    variant="contained"
                  >
                    {translate('RESET')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Typography component="div">
                  {getProfilePage(activeStep)}
                </Typography>
                
                <div className="navigation-controls">
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    className="nav-button btn-back"
                    variant="outlined"
                  >
                    {translate('BACK')}
                  </Button>
                  
                  <div className="nav-spacer"></div>
                  
                  <Button 
                    onClick={handleNext}
                    className="nav-button btn-next"
                    variant="contained"
                  >
                    {translate('NEXT')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default UserBasicProfile;
